import { BloqueResuelto, DocumentoResuelto, FilaCampo } from '@/lib/contratos';
import { classNames } from '@/lib/utils';

interface Props {
  documento: DocumentoResuelto;
  /** Resalta las cláusulas que difieren de la plantilla. */
  resaltarCambios?: boolean;
}

function fechaLarga(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

function Hueco({ fila }: { fila: FilaCampo }) {
  return (
    <span
      className="inline-block align-middle min-w-[240px] h-6 rounded border border-dashed border-amber-400 bg-amber-50 text-[10px] text-amber-700/70 px-1.5 leading-6"
      title={`Campo rellenable en Click & Sign: ${fila.campo}`}
    >
      {fila.campo}
    </span>
  );
}

function Campos({ b }: { b: Extract<BloqueResuelto, { tipo: 'campos' }> }) {
  return (
    <div className="my-3 space-y-2">
      {b.titulo && <p className="font-semibold text-[13.5px]">{b.titulo}</p>}
      {b.filas.map((f, i) => (
        <div key={i} className="text-[13px]">
          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{f.etiqueta}:</span>{' '}
          {f.valor ? <span>{f.valor}</span> : f.campo ? <Hueco fila={f} /> : null}
          {f.nota && <p className="text-[11px] italic text-slate-500 mt-0.5">{f.nota}</p>}
        </div>
      ))}
    </div>
  );
}

function Firmas({ b }: { b: Extract<BloqueResuelto, { tipo: 'firmas' }> }) {
  return (
    <div className="mt-6">
      <p className="text-[13.5px] text-justify mb-4">Y en prueba de conformidad, las partes firman el presente documento.</p>
      <div className="flex flex-wrap gap-6 text-[13px] mb-6">
        {b.lugarFecha.map(f => (
          <div key={f.campo || f.etiqueta} className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{f.etiqueta}:</span>
            {f.valor ? <span>{f.valor}</span> : <Hueco fila={f} />}
          </div>
        ))}
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${b.firmantes.length}, minmax(0, 1fr))` }}>
        {b.firmantes.map(f => (
          <div key={f.rol} className="text-center">
            <div className="h-20 border border-slate-300 rounded-sm bg-slate-50" />
            <p className="text-[12px] font-semibold mt-1.5">{f.etiqueta}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Pinta el documento resuelto tal y como saldrá en el PDF: mismo orden, misma
 * numeración, huecos marcados donde irán los campos rellenables.
 */
export default function VistaPreviaContrato({ documento, resaltarCambios = true }: Props) {
  const marca = (modificada?: boolean) =>
    classNames(resaltarCambios && modificada && 'bg-amber-50 ring-1 ring-amber-200 rounded px-1 -mx-1');

  // text-sereno-dark y no text-[#0B1D2E] (es el mismo color): en modo oscuro
  // `.dark .bg-white` oscurece la hoja, y un color escrito a mano no tiene su
  // versión clara en index.css, así que el texto desaparecía.
  return (
    <article className="bg-white text-sereno-dark rounded-xl border border-slate-200 shadow-sm px-6 py-8 sm:px-12 sm:py-10 font-serif">
      <header className="text-center mb-8">
        <h1 className="text-lg font-bold uppercase leading-snug">{documento.titulo}</h1>
        <p className="text-[11px] text-slate-500 mt-2 font-sans">
          Referencia {documento.codigo} · Documento generado el {fechaLarga(documento.fecha)}
        </p>
      </header>

      {documento.bloques.map((b, i) => {
        switch (b.tipo) {
          case 'titulo':
            return b.nivel === 1
              ? <h2 key={i} className="text-base font-bold mt-10 mb-3 pt-6 border-t border-slate-200">{b.texto}</h2>
              : <h3 key={i} className="text-[15px] font-bold mt-7 mb-2">{b.texto}</h3>;
          case 'subtitulo':
            return <p key={i} className={classNames('font-semibold text-[13.5px] mt-3 mb-1', marca(b.modificada))}>{b.texto}:</p>;
          case 'parrafo':
            return (
              <p key={i} className={classNames('text-[13.5px] leading-relaxed text-justify mb-2', marca(b.modificada))}>
                {b.titulo && <strong>{b.titulo}{b.texto ? ': ' : ''}</strong>}{b.texto}
              </p>
            );
          case 'item': {
            const marcador = b.marca ?? (b.estilo === 'numerada' ? `${b.numero ?? ''}.` : b.nivel === 1 ? '–' : '•');
            return (
              <div key={i} className={classNames('flex gap-2 mb-1.5 text-[13.5px] leading-relaxed', marca(b.modificada))} style={{ marginLeft: b.nivel * 22 }}>
                <span className={classNames('shrink-0 text-right', b.marca ? 'w-8' : 'w-5')}>{marcador}</span>
                <p className="text-justify flex-1">
                  {b.titulo && <strong>{b.titulo}{b.texto ? ': ' : ''}</strong>}{b.texto}
                </p>
              </div>
            );
          }
          case 'tabla':
            return (
              <div key={i} className={classNames('my-3 overflow-x-auto', marca(b.modificada))}>
                <table className="w-full text-[13px] border-collapse">
                  {b.tabla.cabecera && (
                    <thead>
                      <tr>{b.tabla.cabecera.map((c, j) => <th key={j} className="border border-slate-300 bg-slate-100 px-2 py-1 text-left font-semibold">{c}</th>)}</tr>
                    </thead>
                  )}
                  <tbody>
                    {b.tabla.filas.map((f, j) => (
                      <tr key={j}>{f.map((c, k) => <td key={k} className="border border-slate-300 px-2 py-1">{c}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'campos':
            return <Campos key={i} b={b} />;
          case 'firmas':
            return <Firmas key={i} b={b} />;
          default:
            return null;
        }
      })}
    </article>
  );
}
